/*
  Cart drawer lifecycle: closed → opening → open → closing → closed.

  Visibility only. Opening or closing never adds, removes, saves or
  recalculates a cart line, and closing never cancels a cart request that
  is already in flight. Movement is CSS (transform + opacity) keyed off
  [data-state]; see component-cart-drawer.css and bb-cart-glow.css.
*/
const CART_DRAWER_TIMING = {
  openDesktop: 280,
  openMobile: 320,
  close: 220,
  reduced: 100,
  slack: 120,
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.state = 'closed';
    this._token = 0;
    this._inerted = [];
    this._scrollY = 0;
    // Older add-to-cart scripts (grids, recommendations) look for the drawer
    // here; without it their adds never reached the drawer or the header.
    window.cartDrawer = this;

    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);
    this.bindViews();

    // Only an intentional click on the backdrop itself closes the drawer.
    this.overlay = this.querySelector('[id^="CartDrawer-Overlay"]');
    this.overlay?.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.close();
    });
    this.setHeaderCartIconAccessibility();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.open();
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.close();
      });
    }
  }

  connectedCallback() {
    if (!this.hasAttribute('data-state')) this.setAttribute('data-state', 'closed');
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.cart-icon--drawer');
    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  get contentElement() {
    return this.querySelector('[data-drawer-content]');
  }

  get dialogElement() {
    return this.querySelector('[role="dialog"]') || this.contentElement;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  isMobileSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  setState(state) {
    this.state = state;
    this.setAttribute('data-state', state);
  }

  open(triggeredBy) {
    // Remember who opened us so focus can go back there on close.
    const active = document.activeElement;
    const trigger =
      triggeredBy || (active && active !== document.body && !this.contains(active) ? active : null);
    if (trigger) this.setActiveElement(trigger);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Repeated opens (e.g. a second add-to-cart) never stack locks or listeners.
    if (this.state === 'open' || this.state === 'opening') {
      if (!this.contains(document.activeElement)) this.focusInitial();
      return Promise.resolve();
    }

    const fromClosed = this.state === 'closed';
    const token = ++this._token;

    let rtl = typeof theme !== 'undefined' && theme.config && theme.config.rtl === true;
    let from = this.getAttribute('data-drawer-direction') || 'right';
    if (rtl) from = from === 'left' ? 'right' : 'left';
    this.setAttribute('data-bb-from', from);

    if (fromClosed) {
      this._scrollY = window.scrollY;
      this.lockPage();
      this.classList.add('animate', 'active', 'open');
    }
    // Opening starts now; the attribute (and so the slide) follows next frame.
    // A second open() before that frame returns early above.
    this.state = 'opening';

    const duration = this.prefersReducedMotion()
      ? CART_DRAWER_TIMING.reduced
      : this.isMobileSheet()
        ? this.timing('bbOpenMsMobile', CART_DRAWER_TIMING.openMobile)
        : this.timing('bbOpenMsDesktop', CART_DRAWER_TIMING.openDesktop);

    return new Promise((resolve) => {
      const start = () => {
        if (token !== this._token) return resolve();
        this.setState('opening');
        this.focusInitial();
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        this.afterTransition(
          token,
          duration,
          () => {
            this.setState('open');
            resolve();
          },
          resolve
        );
      };
      // Start from the off-screen position on the next frame so the
      // transition always runs; an interrupted close reverses in place.
      if (fromClosed) {
        void this.contentElement?.offsetWidth;
        requestAnimationFrame(start);
      } else {
        start();
      }
    });
  }

  close() {
    if (this.state === 'closed' || this.state === 'closing') return Promise.resolve();

    const token = ++this._token;
    const duration = this.prefersReducedMotion() ? CART_DRAWER_TIMING.reduced : this.timing('bbCloseMs', CART_DRAWER_TIMING.close);
    this.setState('closing');

    return new Promise((resolve) => {
      this.afterTransition(
        token,
        duration,
        () => {
          this.setState('closed');
          this.classList.remove('animate', 'active', 'open');
          this.resetViews();
          this.unlockPage();
          this.restoreFocus();
          document.dispatchEvent(new CustomEvent('cart-drawer:close'));
          resolve();
        },
        resolve
      );
    });
  }

  // Completes on the content's own transitionend, with a timeout fallback for
  // browsers or states where no transition fires. A newer open/close bumps the
  // token, so an interrupted transition settles as stale and never finishes
  // twice or releases a lock the newer transition still needs.
  afterTransition(token, duration, done, stale) {
    const content = this.contentElement;
    let finished = false;
    let timer = null;
    const finish = () => {
      if (finished) return;
      finished = true;
      content?.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      if (token === this._token) done();
      else if (stale) stale();
    };
    const onEnd = (event) => {
      if (event.target === content && (event.propertyName === 'transform' || event.propertyName === 'opacity')) finish();
    };
    content?.addEventListener('transitionend', onEnd);
    timer = setTimeout(finish, duration + CART_DRAWER_TIMING.slack);
  }

  lockPage() {
    if (this._locked) return;
    this._locked = true;
    // Shared with other overlays (bb-overlay-coordinator.js), so a pop-up
    // closing over the open drawer never unlocks the page under it.
    if (window.BBOverlay) {
      window.BBOverlay.lock('cart:drawer', this);
    } else {
      document.body.classList.add('overflow-hidden');
      document.documentElement.setAttribute('scroll-lock', '');
    }

    // The page behind the dialog becomes inert. Overlays that open from the
    // drawer (wishlist board, quick edit, note) live outside these regions
    // and stay usable.
    const background = document.querySelectorAll(
      'body > :is(#header-group, main, footer, .skip-to-content-link, .shopify-section-group-footer-group, .shopify-section-group-header-group)'
    );
    this._inerted = Array.from(background).filter((el) => !el.inert && !el.contains(this));
    this._inerted.forEach((el) => {
      el.inert = true;
    });
  }

  unlockPage() {
    if (!this._locked) return;
    this._locked = false;
    this._inerted.forEach((el) => {
      el.inert = false;
    });
    this._inerted = [];
    if (window.BBOverlay) {
      // Restores the scroll position once no overlay holds the page.
      window.BBOverlay.unlock('cart:drawer');
      return;
    }
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    if (Math.abs(window.scrollY - this._scrollY) > 1) window.scrollTo(0, this._scrollY);
  }

  // Durations set in the theme editor (Cart drawer → Liquid Glow drawer).
  timing(key, fallback) {
    const value = Number(this.dataset[key]);
    return value > 0 ? value : fallback;
  }

  focusInitial() {
    const target =
      this.querySelector('#CartDrawer-Heading') || this.querySelector('.drawer__close') || this.dialogElement;
    target?.focus({ preventScroll: true });
  }

  restoreFocus() {
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    const trigger = this.activeElement;
    const fallback = document.querySelector('.cart-icon--drawer');
    // The control that opened the drawer may have gone (a pop-up that stepped
    // aside for the cart): focus the cart icon instead of nothing.
    const usable = trigger && trigger.isConnected && !trigger.closest('[inert]') && trigger.getClientRects().length > 0;
    const target = usable ? trigger : fallback;
    target?.focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (this.state === 'closed') return;

    if (event.key === 'Escape') {
      event.stopPropagation();
      // Inside an internal view, Escape steps back one level first.
      if (this.viewStack.length) this.backView();
      else this.close();
      return;
    }

    if (event.key !== 'Tab') return;
    const dialog = this.dialogElement;
    if (!dialog) return;
    const focusables = Array.from(
      dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) => (el.offsetParent !== null || el === document.activeElement) && !el.closest('[inert], [hidden]')
    );
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /*
    Internal views (Liquid Glow): order note, gift wrap, shipping estimate,
    discount code and quick variant edit open inside this dialog with a Back
    control, instead of stacking separate pop-ups over the drawer. The cart
    behind a view is inert; Back (or Escape) returns focus to the control
    that opened the view. Views sit outside the regions that re-render, so
    typed text, selections and scroll positions survive cart updates.
  */
  get viewsHost() {
    return this.querySelector('[data-bb-views]');
  }

  get viewStack() {
    if (!this._viewStack) this._viewStack = [];
    return this._viewStack;
  }

  bindViews() {
    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-bb-view-open]');
      if (opener && this.contains(opener)) {
        event.preventDefault();
        this.openView(opener.dataset.bbViewOpen, opener);
        return;
      }
      if (event.target.closest('[data-bb-view-back]')) {
        event.preventDefault();
        this.backView();
      }
    });

    // Quick edit opens here as a view. Capture phase, so the site-wide
    // edit pop-up (document-level listeners) never opens over the drawer.
    this.addEventListener(
      'click',
      (event) => {
        const openEdit = event.target.closest('[data-open-edit-cart]');
        if (openEdit && this.viewsHost?.querySelector('[data-bb-view="variant"]')) {
          event.preventDefault();
          event.stopPropagation();
          this.openVariantView(openEdit);
          return;
        }
        const update = event.target.closest('[data-bb-view="variant"] [data-update-cart-edit]');
        if (update) {
          event.preventDefault();
          event.stopPropagation();
          this.applyVariantChange(update);
        }
      },
      true
    );

    // The coupon wallet is a site-wide pop-up that opens above the drawer.
    // While it is open the drawer goes inert, so Tab and Escape can't reach
    // the cart hidden under it, and focus returns to the row that opened it.
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-bb-open-coupon-popup]');
      if (!trigger || !this.contains(trigger)) return;
      const overlay = document.getElementById('bb-coupon-popup-overlay');
      if (!overlay) return; // no pop-up on this page: the link navigates instead
      this.inert = true;
      // The pop-up has no Tab trap of its own; without one, Tab walks out of it
      // into page content that is not inert (the menu drawer, other pop-ups).
      const trapTab = (e) => {
        if (e.key !== 'Tab' || !overlay.classList.contains('is-visible')) return;
        const modal = overlay.querySelector('.bb-cp-modal') || overlay;
        const items = Array.from(
          modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        const active = document.activeElement;
        const inside = modal.contains(active);
        if (!items.length) {
          e.preventDefault();
          modal.focus();
        } else if (e.shiftKey && (!inside || active === items[0] || active === modal)) {
          e.preventDefault();
          items[items.length - 1].focus();
        } else if (!e.shiftKey && (!inside || active === items[items.length - 1])) {
          e.preventDefault();
          items[0].focus();
        }
      };
      document.addEventListener('keydown', trapTab, true);
      let released = false;
      const release = () => {
        if (released || overlay.classList.contains('is-visible')) return;
        released = true;
        observer.disconnect();
        document.removeEventListener('keydown', trapTab, true);
        this.inert = false;
        // The opener may have been re-rendered or hidden while the pop-up was
        // up; never leave focus stranded in the closed pop-up.
        const top = this.viewStack[this.viewStack.length - 1]?.view;
        const candidates = [
          trigger,
          ...this.querySelectorAll('[data-bb-open-coupon-popup]'),
          top?.querySelector('.bb-glow-view__title'),
        ];
        const target = candidates.find(
          (el) => el?.isConnected && el.getClientRects().length && !el.closest('[inert], [hidden]')
        );
        if (target) target.focus({ preventScroll: true });
        if (!target || document.activeElement !== target) this.focusInitial();
      };
      const observer = new MutationObserver(release);
      observer.observe(overlay, { attributes: true, attributeFilter: ['class'] });
      // If the pop-up never opened (script missing or failed), give the drawer back.
      setTimeout(release, 600);
    });

    this.addEventListener('input', (event) => {
      if (event.target.id !== 'CartDrawer-Note') return;
      const status = this.querySelector('[data-bb-note-status]');
      if (status) status.textContent = event.target.value.trim() ? 'Added' : '';
    });

    // A newly applied or removed code: refresh the code list in the view.
    document.addEventListener('DiscountUpdateEvent', (event) => {
      const html = event.detail?.data?.sections?.[this.dataset.sectionId];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#CartDrawer-View-coupon .cart-discount__codes');
      const current = this.querySelector('#CartDrawer-View-coupon .cart-discount__codes');
      if (fresh && current) current.replaceWith(fresh);
      const status = this.querySelector('[data-bb-view-open="coupon"] .bb-glow-tool__status');
      const freshStatus = doc.querySelector('[data-bb-view-open="coupon"] .bb-glow-tool__status');
      if (status && freshStatus) status.textContent = freshStatus.textContent;
    });

    // Gift wrap added: it is now a cart line, so the offer leaves the list.
    document.addEventListener('GiftWrapUpdateEvent', (event) => {
      const data = event.detail?.data;
      if (!data || data.status) return;
      this.querySelector('[data-bb-view-open="gift"]')?.closest('li')?.remove();
      if (this.viewStack.some((entry) => entry.name === 'gift')) {
        this.resetViews();
        this.focusInitial();
      }
    });
  }

  mainRegions() {
    const host = this.viewsHost;
    const dialog = host?.parentElement;
    if (!dialog) return [];
    return Array.from(dialog.children).filter((el) => el !== host);
  }

  openView(name, trigger) {
    const host = this.viewsHost;
    const view = host?.querySelector(`[data-bb-view="${name}"]`);
    if (!view) return false;
    const stack = this.viewStack;
    if (stack.length && stack[stack.length - 1].view === view) return true;

    if (!stack.length) {
      this.mainRegions().forEach((el) => {
        el.inert = true;
      });
      host.setAttribute('data-active', 'true');
    } else {
      const top = stack[stack.length - 1].view;
      top.inert = true;
      top.setAttribute('data-covered', 'true');
    }

    stack.push({ name, view, trigger });
    trigger?.setAttribute('aria-expanded', 'true');
    view.hidden = false;
    view.inert = false;
    void view.offsetWidth;
    view.setAttribute('data-shown', 'true');
    view.querySelector('.bb-glow-view__title')?.focus({ preventScroll: true });
    if (name === 'shipping') this.loadShippingView(view);
    return true;
  }

  backView({ focus = true } = {}) {
    const stack = this.viewStack;
    const entry = stack.pop();
    if (!entry) return;
    const { view, trigger } = entry;
    trigger?.setAttribute('aria-expanded', 'false');
    view.removeAttribute('data-shown');
    view.inert = true;
    const hide = () => {
      if (!view.hasAttribute('data-shown')) view.hidden = true;
    };
    if (this.prefersReducedMotion() || !focus) hide();
    else setTimeout(hide, 260);

    if (stack.length) {
      const top = stack[stack.length - 1].view;
      top.inert = false;
      top.removeAttribute('data-covered');
    } else {
      this.viewsHost?.removeAttribute('data-active');
      this.mainRegions().forEach((el) => {
        el.inert = false;
      });
    }

    if (!focus) return;
    const fallback = stack.length
      ? stack[stack.length - 1].view.querySelector('.bb-glow-view__title')
      : this.querySelector('#CartDrawer-Heading');
    const target = trigger && trigger.isConnected && !trigger.closest('[inert], [hidden]') ? trigger : fallback;
    target?.focus({ preventScroll: true });
  }

  resetViews() {
    while (this.viewStack.length) this.backView({ focus: false });
  }

  // The country list is large, so the estimator arrives on first open.
  async loadShippingView(view) {
    const body = view.querySelector('[data-bb-shipping-body]');
    const lazy = body?.querySelector('[data-bb-view-lazy]');
    if (!lazy || this._shippingLoading) return;
    this._shippingLoading = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-bb-view="shipping"] [data-bb-shipping-body]');
      if (!fresh || fresh.querySelector('[data-bb-view-lazy]')) throw new Error('missing estimator');
      body.innerHTML = fresh.innerHTML;
    } catch (error) {
      lazy.textContent = "Delivery countries couldn't load. Close and reopen your cart to try again.";
    } finally {
      this._shippingLoading = false;
    }
  }

  // Quick variant edit as a view. Reopening the same line keeps the
  // shopper's unconfirmed choices; nothing changes until they press Update.
  async openVariantView(button) {
    const view = this.viewsHost?.querySelector('[data-bb-view="variant"]');
    const row = button.closest('[data-bb-line-key]');
    if (!view || !row) return;
    const body = view.querySelector('[data-bb-variant-body]');
    const error = view.querySelector('[data-bb-variant-error]');
    const key = row.dataset.bbLineKey;
    const quantity = row.dataset.bbLineQuantity;
    const reuse = view.dataset.lineKey === key && view.dataset.quantity === quantity && body.children.length > 0;

    error.hidden = true;
    error.textContent = '';
    view.dataset.lineKey = key;
    view.dataset.variantId = row.dataset.bbVariantId || '';
    view.dataset.quantity = quantity;
    this.openView('variant', button);
    if (reuse) return;

    body.setAttribute('aria-busy', 'true');
    body.innerHTML = '<p class="bb-glow-view__hint">Loading options…</p>';
    try {
      const response = await fetch(button.dataset.editCartUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (view.dataset.lineKey !== key) return;
      body.innerHTML = html;
      const qtyInput = body.querySelector('.product-edit-item input[name="updates[]"]');
      if (qtyInput) qtyInput.value = quantity;
    } catch (err) {
      body.innerHTML = '';
      view.dataset.lineKey = '';
      error.textContent = "Options couldn't load. Check your connection, then go back and try again.";
      error.hidden = false;
    } finally {
      body.removeAttribute('aria-busy');
    }
  }

  // Swaps the line to the chosen option through BBCart.editLine: the new
  // option is added first and the original only loses the units Shopify
  // actually moved, so a refused or stock-limited option never costs the
  // shopper the item they had. Partial and failed outcomes are explained in
  // the view, with a way to finish the change.
  async applyVariantChange(updateButton) {
    const view = updateButton.closest('[data-bb-view="variant"]');
    const error = view.querySelector('[data-bb-variant-error]');
    const key = view.dataset.lineKey;
    const oldVariantId = Number(view.dataset.variantId);
    const newVariantId = Number(view.querySelector('.product-edit-item input[name="id"]')?.value);
    if (!key || !newVariantId || updateButton.getAttribute('aria-busy') === 'true') return;
    if (newVariantId === oldVariantId) {
      this.backView();
      return;
    }

    const label = updateButton.querySelector('.bb-edit-update-label') || updateButton;
    const original = label.textContent;
    updateButton.setAttribute('aria-busy', 'true');
    updateButton.disabled = true;
    label.textContent = 'Updating cart…';
    this.showVariantMessage(error, '');

    let outcome = null;
    try {
      // The cart is re-rendered inside the same run, so "cart settled"
      // only fires once the drawer already shows the result.
      outcome = await window.BBCart.run(async () => {
        const result = await window.BBCart.editLine({
          lineKey: key,
          targets: (line) => new Map([[newVariantId, line.quantity]]),
          sections: this.dataset.sectionId,
        });
        if (result.json) this.applySections(result.json);
        else if (result.status !== 'refused') await this.refreshItems();
        return result;
      });
    } catch (err) {
      outcome = { status: 'unknown' };
    } finally {
      updateButton.removeAttribute('aria-busy');
      updateButton.disabled = false;
      label.textContent = original;
    }

    const moved = outcome.results && outcome.results[0];
    const from = (outcome.original && outcome.original.variant_title) || 'your original option';
    switch (outcome.status) {
      case 'ok':
        view.dataset.lineKey = '';
        view.querySelector('[data-bb-variant-body]').innerHTML = '';
        this.backView();
        return;
      case 'partial':
        this.showVariantMessage(
          error,
          `Only ${moved.landed} of ${moved.requested} could change to ${moved.title || 'that option'}. The other ${outcome.shortfall} stay as ${from}.`
        );
        return;
      case 'refused':
        this.showVariantMessage(error, outcome.message || "That option couldn't be added in that quantity. Your cart is unchanged.");
        return;
      case 'remove-failed':
      case 'unfinished':
        this.showVariantMessage(
          error,
          outcome.status === 'unfinished'
            ? `Your last change to this item didn't finish: ${(moved && moved.title) || 'the new option'} and ${from} are both in your cart. Fix that first.`
            : `${(moved && moved.title) || 'The new option'} is in your cart, but ${from} couldn't be updated, so both are in your cart.`,
          'Fix my cart',
          async () => {
            // Re-reads the cart and only finishes the change if nothing moved
            // since (BBCart.finishEdit). Never adds the new option again.
            const fixed = await window.BBCart.run(async () => {
              const result = await outcome.retry({ sections: this.dataset.sectionId });
              if (result.json && result.json.sections) this.applySections(result.json);
              else await this.refreshItems();
              return result;
            });
            if (fixed.status === 'done') {
              this.backView();
              return null;
            }
            if (fixed.status === 'changed') return 'Your cart changed since then, so nothing was removed. Check your cart below.';
            throw new Error('not finished');
          }
        );
        return;
      case 'missing':
        this.showVariantMessage(error, 'This item changed in another window. Your cart below is up to date.');
        return;
      default:
        this.showVariantMessage(error, "We couldn't confirm your cart. Check your connection; your cart below shows what Shopify has.");
    }
  }

  showVariantMessage(box, message, actionLabel, action) {
    box.textContent = message;
    box.hidden = !message;
    if (!message || !actionLabel) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bb-glow-inline-action';
    button.textContent = actionLabel;
    button.addEventListener('click', async () => {
      // One attempt at a time: further clicks do nothing until it settles.
      if (button.disabled) return;
      button.disabled = true;
      button.textContent = 'Updating cart…';
      try {
        const note = await action();
        if (note) box.textContent = note;
        else box.hidden = true;
      } catch (err) {
        button.disabled = false;
        button.textContent = actionLabel;
      }
    });
    box.append(' ', button);
  }

  // Puts a response that carries rendered sections on screen and tells the
  // rest of the page, marked as already applied so nobody renders it twice.
  applySections(json, source = 'cart-drawer') {
    const items = this.querySelector('cart-drawer-items');
    const revision = window.BBCart ? window.BBCart.nextRevision() : 0;
    if (json.sections && items && typeof items.updateSections === 'function') {
      items.updateSections(json.sections, revision);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: Object.assign({}, json, { source, revision, bbApplied: true }) }));
    }
    if (json.item_count !== undefined && window.BBCart) window.BBCart.renderCounts(json.item_count);
  }

  // Sections only (no cart JSON): rendered here; listeners get the flag so
  // they don't treat this as a complete cart or render it again.
  async refreshItems() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(this.dataset.sectionId)}`, { cache: 'no-store' });
      const sections = await response.json();
      this.applySections({ sections }, 'cart-drawer-refresh');
    } catch (error) {
      /* the settled-cart check retries on the next change */
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /*
    Puts a freshly rendered drawer section on screen. The whole dialog body
    (and the drawer's own is-empty state) is replaced when the cart changes
    between empty and populated, when a populated drawer is missing its
    More options views, or when force is set (a confirmed add). Otherwise it
    returns false and the caller updates only the list and footer, which
    keeps scroll position and open views.
  */
  applyDrawerHTML(html, { force = false } = {}) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('#CartDrawer');
    if (!fresh) return false;
    const freshEmpty = !!fresh.querySelector('.drawer__inner-empty');
    const shownEmpty = !!this.querySelector('#CartDrawer .drawer__inner-empty');
    const missingViews = !freshEmpty && !!fresh.querySelector('[data-bb-views]') && !this.viewsHost;
    if (!force && freshEmpty === shownEmpty && !missingViews) return false;

    const target = this.querySelector('#CartDrawer');
    if (!target) return false;
    const hadFocus = target.contains(document.activeElement);

    // Recommendations load after the drawer renders; keep the ones on screen.
    const recommendations = this.querySelector('#CartDrawer-CartItems .cart-drawer__recommendations');
    const preserved = recommendations ? recommendations.outerHTML : null;

    // Views are rebuilt with the body; none stays open over a new cart.
    this._viewStack = [];
    target.innerHTML = fresh.innerHTML;

    const freshDrawer = doc.querySelector('cart-drawer');
    this.classList.toggle('is-empty', freshDrawer ? freshDrawer.classList.contains('is-empty') : freshEmpty);

    if (preserved) {
      const container = this.querySelector('#CartDrawer-CartItems');
      if (container && !container.querySelector('.cart-drawer__recommendations')) {
        const status = container.querySelector('p[role="status"]');
        if (status) status.insertAdjacentHTML('beforebegin', preserved);
        else container.insertAdjacentHTML('beforeend', preserved);
      }
    }

    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    // Focus was on a control that no longer exists (the last item's remove).
    if (hadFocus && this.state !== 'closed' && !this.contains(document.activeElement)) this.focusInitial();
    return true;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    const html = parsedState.sections && (parsedState.sections[this.dataset.sectionId] || parsedState.sections['cart-drawer']);
    if (html) this.applyDrawerHTML(html, { force: true });

    // A confirmed add: the response carries the line key Shopify accepted.
    document.dispatchEvent(
      new CustomEvent('cart:item-added', {
        detail: {
          key: parsedState.key || null,
          keys: Array.isArray(parsedState.items) ? parsedState.items.map((item) => item.key) : [],
        },
      })
    );

    // The overlay listener is bound once in the constructor; rebinding it here
    // on every add stacked duplicate close handlers.
    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  // The drawer's own section id: the one in the popup group, with the
  // merchant's settings. 'cart-drawer' rendered the bare section file with
  // its defaults. Header counts are set by BBCart.renderCounts, which keeps
  // every header variant intact (the cart-icon-bubble section replaced the
  // header icon's whole markup).
  getSectionsToRender() {
    return [
      {
        id: this.dataset.sectionId || 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class ModalComponent extends HTMLElement {
  constructor() {
    super();
    this.querySelector('#Modal-Overlay')?.addEventListener('click', this.close.bind(this));

    const modalId = `#${this.id}`;
    const toggleButtons = document.querySelectorAll(`[data-drawer-toggle="${modalId}"]`);

    toggleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.show(button);
      });
    });

    // Shopify Design Mode
    if (Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.addEventListener('shopify:block:select', () => (this.isOpen = true));
      this.addEventListener('shopify:block:deselect', () => (this.isOpen = false));
    }
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;
      if (this.isConnected) {
        this._animate(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
    this.setAttribute('aria-expanded', value ? 'true' : 'false');
  }

  show(opener) {
    this.openedBy = opener;
    this.isOpen = true;
    this.trapFocus();
  }

  close() {
    this.isOpen = false;
    removeTrapFocus(this.openedBy);
  }

  async _animate(open) {
    const inner = this.querySelector('.modal__inner');
    const content = this.querySelector('.modal-content');
    const header = this.querySelector('.modal-header');
    this.style.overflow = 'hidden';

    if (open) {
      this.setAttribute('open', '');
      await Motion.timeline([
        [inner, { opacity: [0, 1], transform: ['translateY(2rem)', 'translateY(0)'] }, { duration: 0.4, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
        [content, { opacity: [0, 1], transform: ['translateY(1rem)', 'translateY(0)'] }, { duration: 0.3, at: '-0.15' }],
        [header, { opacity: [0, 1], transform: ['translateY(-0.5rem)', 'translateY(0)'] }, { duration: 0.3, at: '-0.2' }],
      ]).finished;
    } else {
      await Motion.timeline([
        [header, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-0.5rem)'] }, { duration: 0.2 }],
        [content, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(1rem)'] }, { duration: 0.2 }],
        [inner, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(2rem)'] }, { duration: 0.25, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
      ]).finished;
      this.removeAttribute('open');
    }

    this.style.overflow = 'visible';
  }

  trapFocus() {
    this.addEventListener(
      'transitionend',
      () => {
        const container = this.querySelector('.modal__inner');
        const focusEl = this.querySelector('.modal__close');
        trapFocus(container, focusEl);
      },
      { once: true }
    );
  }
}

if (!customElements.get('modal-component')) {
  customElements.define('modal-component', ModalComponent);
}
